import { EventEmitter, Injectable, OnDestroy, Output } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { UserMemberService } from '~app/services/user-member.service';
import { CartActions } from '../../../../../shopping-cart/cart';
import { Product } from '../../../../../shopping-cart/product';
import { FormDefinition } from '../../../../shared/model/company.model';
import { Address } from '../model/address.model';
import { AnnualDeclaration, AnnualDeclarationFormGroupEnum } from '../model/annual-declaration.model';
import { AttachedDocuments, BaseShareholder, CompanyAmalgamation, CompanyEstablishmentMember, CompanyIdentification, CompanyNonVotingNaturalPersons, CompanyOtherEstablishment, CompanyOtherName, CompanyRepresentativeSubstitute, LegalPerson, MainPartnershipCompany, NaturalPerson, PartnershipCompany, ShareHolderMember, ShareHolderNaturalPerson } from '../model/company-identification.model';
import { LegalEntityDocumentTypeName } from '../model/legal-entity-document.model';
import { LegalForm } from '../model/legal-persons-information-none-voting.model';
import { Member } from '../model/member.model';
import { NaturalPersonMemberOrParent } from '../model/natural-persons-info-none-voting.model';
import { InitAnnualDeclarationForm, SaveAnnualDeclarationForm } from '../state/annual-declaration.actions';
import { AnnualDeclarationState } from '../state/annual-declaration.state';
import { AnnualDeclarationDataService } from './annual-declaration-data.service';
import { AnnualDeclarationFormFirebaseService } from './annual-declaration-form-firebase.service';

const VISITED_STEPS = 'visitedSteps';

@Injectable()
export class AnnualDeclarationFormService implements OnDestroy {

  @Select(AnnualDeclarationState.selectedLegalEntityId)
  selectedLegalEntityId$: Observable<string>;

  annualDeclarationDefinition: FormDefinition;
  annualDeclarationForm: UntypedFormGroup;
  uploadUrl: string;
  reset = false;
  lastSaveDate = new Date();
  memberId: string;
  private trials = 0;
  private trials2 = 0;

  private wasVisited: boolean[];
  @Output()
  allStepVisited: EventEmitter<string> = new EventEmitter<string>();
  destroy$ = new Subject<void>();

  constructor(
    private annualDeclarationDataService: AnnualDeclarationDataService,
    private annualDeclarationFormFirebaseService: AnnualDeclarationFormFirebaseService,
    private userService: UserMemberService,
    private formBuilder: UntypedFormBuilder,
    private toastrService: ToastrService,
    private store: Store,
    private translate: TranslateService,
  ) {
    this.annualDeclarationForm = this.formBuilder.group({});

    this.userService.memberId$.subscribe(memberId => { this.memberId = memberId; });
    this.initWasVisited();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isValid(): boolean {
    return (this.annualDeclarationForm.valid);
  }

  /*
   After a form is initialized, we link it to our main form
  */
  formInitialized(name: AnnualDeclarationFormGroupEnum, form: UntypedFormGroup) {
    this.annualDeclarationForm.setControl(name, form);
  }

  saveForm() {
    const annualDeclaration = this.getCurrentAnnualDeclarationFromForm();
    this.store.dispatch(new SaveAnnualDeclarationForm(annualDeclaration));
    this.showSuccess(this.translate.instant('form.saveToast.title'), this.translate.instant('form.saveToast.message'));
  }

  initForm(legalEntityId: string) {
    this.store.dispatch(new InitAnnualDeclarationForm(legalEntityId));
    this.showSuccess(this.translate.instant('form.initToast.title'), this.translate.instant('form.initToast.message'));
  }

  /**
   * Build AnnualDeclaration object from formGroup
   */
  private getCurrentAnnualDeclarationFromForm(): AnnualDeclaration {
    const annualDeclaration = new AnnualDeclaration();

    annualDeclaration.identification = this.getCompanyIdentificationFromForm();
    annualDeclaration.otherNames.otherNames = this.getCompanyOtherNamesFromForm();
    annualDeclaration.representativeSubstitute = this.getCompanyRepresentativeSubstituteFromForm();
    annualDeclaration.otherEstablishments.otherEstablishments = this.getCompanyOtherEstablishmentsFromForm();
    annualDeclaration.directors.directorsNumber = this.getCompanyDirectorsFromForm();
    annualDeclaration.votingMembers.votingMembers = this.getCompanyVotingMembersFromForm();
    annualDeclaration.votingLegalPersons.legalPersons = this.getCompanyVotingLegalPersonsFromForm();
    annualDeclaration.nonVotingNaturalPersons = this.getCompanyNonVotingNaturalPersonsFromForm();
    annualDeclaration.nonVotingLegalPersons.legalPersons = this.getCompanyNonVotingLegalPersonsFromForm();
    annualDeclaration.amalgamation = this.getAmalgamation();
    annualDeclaration.annualDeclaractionFormAttachment = this.getAttachedDocuments();

    return annualDeclaration;
  }

  private getCompanyIdentificationFromForm(): CompanyIdentification {
    const res = new CompanyIdentification();
    const identificationFrom = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyIdentification] as UntypedFormGroup;
    if (identificationFrom) {
      res.name = identificationFrom.get('name').value;
      res.neq = identificationFrom.get('neq').value;
      res.address = identificationFrom.get('address').value;
    }
    return res;

  }

  private getCompanyOtherNamesFromForm(): CompanyOtherName[] {
    let res: CompanyOtherName[] = [];
    const otherNamesFromForm = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyOtherNames] as UntypedFormGroup;

    if (otherNamesFromForm && otherNamesFromForm.value) {
      const companyArray = otherNamesFromForm.value.otherCompany;

      res = companyArray.map(elt => {
        const obj = new CompanyOtherName();
        obj.id = elt.id;
        obj.name = elt.name;
        obj.startDate = elt.startDate;
        obj.endDate = elt.endDate;
        return obj;
      });
    }
    return res;

  }

  private getCompanyRepresentativeSubstituteFromForm(): CompanyRepresentativeSubstitute {
    const res = new CompanyRepresentativeSubstitute();
    const representativeSubstituteFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyRepresentativeSubstitute] as UntypedFormGroup;

    if (representativeSubstituteFrom) {
      res.representative = representativeSubstituteFrom.get('representative').value;
      res.representative.response = representativeSubstituteFrom.get('response').value;
      res.substitute = representativeSubstituteFrom.get('substitute').value;
      res.substitute.response = representativeSubstituteFrom.get('responseSubstitut').value;
      res.certified = representativeSubstituteFrom.get('certified').value;
      res.attestationDate = representativeSubstituteFrom.get('attestationDate').value;
    }
    return res;

  }

  public getCompanyOtherEstablishmentsFromForm(): CompanyOtherEstablishment[] {
    let res: CompanyOtherEstablishment[] = [];
    const otherEstablishmentsFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyOtherEstablishments] as UntypedFormGroup;

    if (otherEstablishmentsFrom && otherEstablishmentsFrom.value) {
      const establishmentArray = otherEstablishmentsFrom.value.otherEstablishments;

      res = establishmentArray.map(elt => {
        const obj = new CompanyOtherEstablishment();
        obj.name = elt.name;
        obj.address = elt.address;
        obj.exploitedByLegalEntity = elt.exploitedByLegalEntity;
        obj.members = elt.memberList.map(member => {
          const etbsMember = new CompanyEstablishmentMember();
          etbsMember.state = member?.state;
          etbsMember.responsibility = member.responsibility;
          etbsMember.permitNumber = member.permitNumber;
          etbsMember.firstName = member.firstName;
          etbsMember.lastName = member.lastName;
          etbsMember.changeDate = member.changeDate;
          etbsMember.modificationType = member.modificationType;
          etbsMember.modificationEffectiveDate = member.modificationEffectiveDate
          return etbsMember;
        });
        return obj;
      });
    }
    return res;
  }

  private getCompanyDirectorsFromForm(): number {
    const directorsFromForm = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyDirectors] as UntypedFormGroup;
    const res = directorsFromForm?.value?.numberDirectors;
    return res;
  }

  private getCompanyVotingMembersFromForm(): ShareHolderMember[] {
    let res: ShareHolderMember[] = [];
    const votingMembersFrom = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyVotingMembers] as UntypedFormGroup;

    if (votingMembersFrom && votingMembersFrom.value) {
      const valuesMembersArray = votingMembersFrom.value.valuesMembers;

      res = valuesMembersArray.map(formMember => {
        const obj = new ShareHolderMember();
        obj.permitNumber = formMember.permitNumber;
        obj.firstName = formMember.firstName;
        obj.lastName = formMember.lastName;
        obj.startDate = formMember.effectiveDate;
        obj.endDate = formMember.endDate;
        obj.sharePercentage = Number(formMember.votingNumber);
        obj.address = formMember.address;
        obj.confirmation = formMember.confirmation;
        return obj;
      });
    }
    return res;
  }

  private getCompanyVotingLegalPersonsFromForm(): LegalPerson[] {
    let res: LegalPerson[] = [];
    const votingLegalPersonstsFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyVotingLegalPersons] as UntypedFormGroup;

    if (votingLegalPersonstsFrom && votingLegalPersonstsFrom.value) {
      const legalPersonArray = votingLegalPersonstsFrom.value.values;

      res = legalPersonArray.map(legalPersForm => {
        const obj = new LegalPerson();
        obj.name = legalPersForm.name;
        obj.neq = legalPersForm.neq;
        obj.type = legalPersForm.type ? legalPersForm.type : LegalForm.None;
        obj.numberOfTrustees = legalPersForm.numberOfTrustees ? legalPersForm.numberOfTrustees : 0;
        obj.startDate = legalPersForm.effectiveDate;
        obj.endDate = legalPersForm.endDate;
        obj.sharePercentage = Number(legalPersForm.votingShares);
        obj.address = legalPersForm.headOffice;
        obj.members = legalPersForm.statuses;

        return obj;
      });
    }
    return res;
  }

  private getCompanyNonVotingNaturalPersonsFromForm(): CompanyNonVotingNaturalPersons {
    const res = new CompanyNonVotingNaturalPersons();
    const nonVotingNaturalPersonsFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyNonVotingNaturalPersons] as UntypedFormGroup;

    if (nonVotingNaturalPersonsFrom && nonVotingNaturalPersonsFrom.value) {
      const membersAndNaturalPersonArray = nonVotingNaturalPersonsFrom.value.values;

      res.members = [];
      res.naturalPersons = [];

      function fillBaseShareHolder(shareHolder: BaseShareholder, value: any) {
        shareHolder.startDate = value.effectiveDate;
        shareHolder.endDate = value.endDate;
        shareHolder.sharePercentage = Number(value.votingShares);
        shareHolder.address = value.address;
      }

      membersAndNaturalPersonArray.forEach(formShareHolder => {
        if (formShareHolder.type === NaturalPersonMemberOrParent.Member) {
          const member = new ShareHolderMember();

          fillBaseShareHolder(member, formShareHolder);
          member.permitNumber = formShareHolder.permitNumber;
          member.lastName = formShareHolder.lastName;
          member.firstName = formShareHolder.firstName;

          res.members.push(member);
        } else if (formShareHolder.type === NaturalPersonMemberOrParent.Parent) {
          const naturalPers = new ShareHolderNaturalPerson();

          fillBaseShareHolder(naturalPers, formShareHolder);
          naturalPers.lastName = formShareHolder.lastName;
          naturalPers.firstName = formShareHolder.firstName;
          naturalPers.relatedMember = {
            permitNumber: formShareHolder.relatedMember.permitNumber,
            firstName: formShareHolder.relatedMember.firstName,
            lastName: formShareHolder.relatedMember.lastName,
          };
          naturalPers.relationType = formShareHolder.relatedMember.relationship;

          res.naturalPersons.push(naturalPers);
        }
      });
    }
    return res;
  }

  private getCompanyNonVotingLegalPersonsFromForm(): LegalPerson[] {
    let res: LegalPerson[] = [];
    const nonVotingLegalPersonsFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyNonVotingLegalPersons] as UntypedFormGroup;

    if (nonVotingLegalPersonsFrom && nonVotingLegalPersonsFrom.value) {
      const legalPersonArray = nonVotingLegalPersonsFrom.value.values;

      res = legalPersonArray.map(legalPerson => {
        const obj = new LegalPerson();
        obj.name = legalPerson.legalForm.legalEntityName;
        obj.neq = legalPerson.legalForm.neq;
        obj.type = legalPerson.legalForm.type ? legalPerson.legalForm.type : LegalForm.None;
        obj.startDate = legalPerson.legalForm.effectiveDate;
        obj.endDate = legalPerson.legalForm.endingDate;
        obj.sharePercentage = Number(legalPerson.legalForm.nonVotingShares);
        obj.address = legalPerson.headOfficeAddress;
        obj.numberOfTrustees = legalPerson.trusteeNumber ? legalPerson.trusteeNumber : 0;
        obj.members = legalPerson.odqMembers;

        if (legalPerson.spouseRelatives) {
          obj.naturalPersons = legalPerson.spouseRelatives.map(relative => {
            const natPers = new NaturalPerson();
            natPers.relatedMember = relative.relatedMember;
            natPers.relationType = relative.relationType;
            natPers.firstName = relative.firstName;
            natPers.lastName = relative.lastName;
            return natPers;
          });
        }
        return obj;
      });
    }
    return res;
  }

  getAmalgamation(): CompanyAmalgamation {
    const amalgamation = new CompanyAmalgamation();
    amalgamation.partnershipCompany = new MainPartnershipCompany();
    const amalgamationForm = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyAmalgamation] as UntypedFormGroup;
    if (amalgamationForm && amalgamationForm.value && amalgamationForm.value.partnershipCompany) {
      amalgamation.partnershipCompany.name = amalgamationForm.value.partnershipCompany.name;
      amalgamation.partnershipCompany.neq = amalgamationForm.value.partnershipCompany.neq;
      amalgamation.partnershipCompany.dateOfAmalgamation = amalgamationForm.value.partnershipCompany.dateOfAmalgamation;
      amalgamation.otherPartnershipCompany = amalgamationForm.value.otherPartnershipCompany.map(otherCompanyForm => {
        const company = new PartnershipCompany();
        company.name = otherCompanyForm.name;
        company.neq = otherCompanyForm.neq;
        return company;
      });
    }
    return amalgamation;
  }

  getAttachedDocuments(): AttachedDocuments[] {
    // return attachedDocuments as it was
    return this.annualDeclarationFormFirebaseService.attachedDocumentsBuffer;
  }

  getNumberOfEstablishments(): number {

    let res = 0;
    const otherEstablishmentsFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyOtherEstablishments] as UntypedFormGroup;

    if (otherEstablishmentsFrom && otherEstablishmentsFrom.value) {
      res = otherEstablishmentsFrom.value.otherEstablishments.filter(ets => {
        // checks that at least name and line 1 of adress are filled.
        const isNotEmpty = (ets.name && ets.name.length > 0) && (ets.address && ets.address.line1.length > 0);
        return isNotEmpty && ets.exploitedByLegalEntity;
      }).length;
    }
    return res;
  }

  getRespondentPermitNumber(): string {
    let res = '';
    const representativeSubstituteFrom =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyRepresentativeSubstitute] as UntypedFormGroup;

    if (representativeSubstituteFrom) {
      res = representativeSubstituteFrom.get('representative').value.permitNumber;
    }
    return res;
  }

  getAddress(): Address {
    return this.getCompanyIdentificationFromForm().address;
  }

  getAdressFormForm(formGroup: UntypedFormGroup): Address {
    const address = new Address();
    address.line1 = formGroup.get('line1').value;
    address.line2 = formGroup.get('line2').value;
    address.city = formGroup.get('city').value;
    address.provinceCode = formGroup.get('provinceCode').value;
    address.postalCode = formGroup.get('postalCode').value;
    address.countryCode = formGroup.get('countryCode').value;
    return address;
  }

  getMemberFromForm(formGroup: UntypedFormGroup): Member {
    const member = new Member();
    member.permitNumber = formGroup.get('permitNumber').value;
    member.firstName = formGroup.get('firstName').value;
    member.lastName = formGroup.get('lastName').value;
    member.changeDate = formGroup.get('changeDate') ? formGroup.get('changeDate').value : null;
    return member;
  }

  createMemberFormGroup(member: Member): UntypedFormGroup {
    return this.formBuilder.group({
      permitNumber: [member.permitNumber],
      firstName: [member.firstName],
      lastName: [member.lastName],
      changeDate: [member.changeDate],
    });
  }

  createAddressFormGroup(address: Address): UntypedFormGroup {
    address = address || new Address();
    return this.formBuilder.group({
      line1: address.line1,
      line2: address.line2,
      city: address.city,
      provinceCode: address.provinceCode,
      postalCode: address.postalCode,
      countryCode: address.countryCode,
    });
  }

  preparePayment(legalEntityNumber: string) {
    this.store.dispatch(new CartActions.ClearCart());

    this.annualDeclarationDataService.getProducts(legalEntityNumber, this.getNumberOfEstablishments(), this.memberId).pipe(
      take(1),
      takeUntil(this.destroy$),
    ).subscribe((response: any) => {
      response?.forEach(element => {
        const newProduct: Product = {
          description: element.description,
          id: element.id,
          totalPrice: element.totalAmount,
          societeId: element.noSociete,
          title: '',
          quantity: element.quantity,
          unitPrice: element.unitAmount,
        };

        this.store.dispatch(new CartActions.AddProductToCart(newProduct));
      });
    });
  }

  private showError(title: string, message: string) {
    this.toastrService.error(
      message,
      title,
      {
        closeButton: true,
        timeOut: 4000,
        onActivateTick: true,
      },
    );
  }

  private showInformation(title: string, message: string) {
    this.toastrService.info(
      message,
      title,
      {
        closeButton: true,
        timeOut: 4000,
        onActivateTick: true,
      },
    );
  }

  private showSuccess(title: string, message: string) {
    this.toastrService.success(
      message,
      title,
      {
        closeButton: true,
        timeOut: 4000,
        onActivateTick: true,
        positionClass: 'toast-bottom-left',
      },
    );
  }

  public round(value: number, decimals: number) {
    return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
  }

  /**
   *  2875 - Validate number of trustees before next step. Let go after 2 trials
   */
  public iskTrusteesCountValid(): boolean {
    // Run validation and count errors
    const legalPersonVotingForm =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyVotingLegalPersons] as UntypedFormGroup;
    const formArray: UntypedFormArray = legalPersonVotingForm.controls.values as UntypedFormArray;
    const formGroups = formArray.controls;
    const errors = formGroups.filter(valueCtrl => {
      valueCtrl.updateValueAndValidity();
      return valueCtrl.errors && valueCtrl.errors.countOfTrusteesErrors;
    });
    let res = errors.length === 0;

    if (!res && this.trials < 2) {
      this.trials++;
    } else {
      res = true;
    }
    return res;
  }

  /**
   * 2864 : Sum of voting parts must equal 100%
   */
  isVotingPartsTotalsValid(): boolean {
    return this.getAllVotingPartsTotal() === 100;
  }

  getAllVotingPartsTotal(): number {
    return this.round(this.getMembersVotingPartsTotal() + this.getLegalPersonsVotingPartsTotal(), 10);
  }

  getMembersVotingPartsTotal(): number {
    let res = 0;
    // Try / catch : If parts of the forms are null or undefined, return  0;
    try {

      const legalMemberVotingForm = this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyVotingMembers] as UntypedFormGroup;
      res = legalMemberVotingForm.value.valuesMembers.reduce((acc, curr) => {
        // Ignore percentages with end Dates
        if (curr.endDate) { return acc; }
        return acc + Number(curr.votingNumber || 0);
      }, 0);

    } catch (e) {
      if (e instanceof TypeError) {
        res = 0;
      }
    }

    return this.round(res, 10);
  }

  getLegalPersonsVotingPartsTotal(): number {
    let res = 0;

    // Try / catch : If parts of the forms are null or undefined, return  0;
    try {
      const legalPersonVotingForm =
        this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyVotingLegalPersons] as UntypedFormGroup;
      res = legalPersonVotingForm.value.values.reduce((acc, curr) => {
        // Ignore percentages with end Dates
        if (curr.endDate) { return acc; }
        return acc + Number(curr.votingShares || 0);
      }, 0);

    } catch (e) {
      if (e instanceof TypeError) {
        res = 0;
      }
    }
    return this.round(res, 10);
  }

  /**
   * 2880 -  Validate number of trustees before next step. Let go after 2 trials
   */
  isNonVotingTrusteesCountValid(): boolean {
    // Run validation and count errors
    const legalPersonVotingForm =
      this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyNonVotingLegalPersons] as UntypedFormGroup;
    const formArray: UntypedFormArray = legalPersonVotingForm.controls.values as UntypedFormArray;
    const formGroups = formArray.controls;
    const errors = formGroups.filter(valueCtrl => {
      valueCtrl.updateValueAndValidity();
      return valueCtrl.errors && valueCtrl.errors.countOfTrusteesErrors;
    });
    let res = errors.length === 0;

    if (!res && this.trials2 < 2) {
      this.trials2++;
    } else {
      res = true;
    }
    return res;
  }

  /**
   * 2864 : Sum of non-voting parts must equal 100%
   */
  isNonVotingPartsTotalsValid(): boolean {
    return this.getNonVotingPartsTotal() === 100 || this.getNonVotingPartsTotal() === 0;
  }

  getNonVotingPartsTotal(): number {
    return this.round(this.getMembersNonVotingPartsTotal() + this.getLegalPersonsNonVotingPartsTotal(), 10);
  }

  getMembersNonVotingPartsTotal(): number {
    let res = 0;
    // Try / catch : If parts of the forms are null or undefined, return  0;
    try {

      const legalMemberNonVotingForm =
        this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyNonVotingNaturalPersons] as UntypedFormGroup;
      res = legalMemberNonVotingForm.value.values.reduce((acc, curr) => {
        // Ignore percentages with end Dates
        if (curr.endDate) { return acc; }
        return acc + Number(curr.votingShares || 0);
      }, 0);

    } catch (e) {
      if (e instanceof TypeError) {
        res = 0;
      }
    }

    return this.round(res, 10);
  }

  getLegalPersonsNonVotingPartsTotal(): number {
    let res = 0;

    // Try / catch : If parts of the forms are null or undefined, return  0;
    try {
      const legalPersonNonVotingForm =
        this.annualDeclarationForm.controls[AnnualDeclarationFormGroupEnum.CompanyNonVotingLegalPersons] as UntypedFormGroup;
      res = legalPersonNonVotingForm.value.values.reduce((acc, curr) => {
        // Ignore percentages with end Dates
        if (curr.legalForm.endingDate) { return acc; }
        return acc + Number(curr.legalForm.nonVotingShares || 0);
      }, 0);

    } catch (e) {
      if (e instanceof TypeError) {
        res = 0;
      }
    }
    return this.round(res, 10);
  }

  isMandatoryDocumentsUploaded() {
    const attachedDocuments = this.getAttachedDocuments();

    if (!attachedDocuments?.length) {
      return false;
    }

    let companyInformationStatementDocumentCount = 0;
    attachedDocuments.forEach(document => document.documentType === LegalEntityDocumentTypeName.CompanyInformationStatementAtREQ ?
      companyInformationStatementDocumentCount++ : null);

    return companyInformationStatementDocumentCount > 0;
  }

  private initWasVisited() {
    // default value
    this.wasVisited = new Array(5).fill(false);
    this.wasVisited[0] = true;
    // Get value from storage if exist
    this.selectedLegalEntityId$.pipe(
      filter(id => !!id),
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(id => {
      const savedconsultedStep = localStorage.getItem(`${VISITED_STEPS}-${id}`);
      if (savedconsultedStep) {
        this.wasVisited = JSON.parse(savedconsultedStep);
      }
    });
  }

  private setWasVisitedStorage() {
    // save consulted states
    this.selectedLegalEntityId$.pipe(
      filter(id => !!id),
      take(1),
      takeUntil(this.destroy$),
    ).subscribe(id => {
      localStorage.setItem(`${VISITED_STEPS}-${id}`, JSON.stringify(this.wasVisited));
    });
  }

  setStepVisited(step: number) {
    this.wasVisited[step] = true;
    this.setWasVisitedStorage();
    if (this.allStepsVisited) {
      this.allStepVisited.emit('All Steps Visited');
    }
  }

  getStepVisited(step: number): boolean {
    return this.wasVisited[step];
  }

  get allStepsVisited(): boolean {
    return this.wasVisited.reduce((acc, curr) => acc && curr, true);
  }

}
